import {
  readImportedTopologyFaceTriangleRangeBindings,
  readImportedTriangleRangeTuple,
} from '../loaders/imported-topology-face-range-binding'
import type { ImportedTopologyFaceTriangleRangeBinding } from '../loaders/types'
import { asRecord } from '../loaders/utils'

/**
 * topo sidecar 交互条目的诊断信息。
 */
export interface ImportedTopoSidecarEntryDiagnostics {
  /** 原始 indices 是否只是 0..N 的顺序展开。 */
  sequentialIndexBuffer: boolean
  /** `topologySnapshot.faces[].triangleRange` 与 `meshData.faceTriangleRanges` 不一致的面数量。 */
  topologyFaceRangeMismatchCount: number
}

/**
 * topo sidecar 交互 payload。
 */
export interface ImportedInteractiveTopoSidecarPayload {
  /** body 级稳定 ID。 */
  bodyId?: string
  /** 后端提供的拓扑对象 ID。 */
  objectId?: string
  /** 可直接交给 FeatureResultMesh 的网格化拓扑数据。 */
  meshData: MeshData
  /** 面到 GLB 可见三角区间的显式映射。 */
  visibleFaceTriangleRanges: ImportedTopologyFaceTriangleRangeBinding[]
  /** 解析阶段产生的协议诊断信息。 */
  diagnostics: ImportedTopoSidecarEntryDiagnostics
}

/**
 * topo sidecar 交互 entry。
 */
export interface ImportedTopoSidecarEntry {
  /** entry 在 sidecar entries 表中的 key。 */
  key: string
  /** entry 编码仅作为诊断元数据，不参与前端是否读取的判断。 */
  encoding?: string
  /** entry 声明的字节长度。 */
  byteLength?: number
  /** entry 声明的内容哈希。 */
  sha256?: string
  /** 已归一化的交互拓扑 payload。 */
  payload: ImportedInteractiveTopoSidecarPayload
}

/**
 * topo sidecar 文档。
 */
export interface ImportedTopoSidecarDocument {
  /** 按 entry key 建立的交互 entry 映射。 */
  entries: Record<string, ImportedTopoSidecarEntry>
  /** 交互 entry 列表。 */
  interactiveEntries: ImportedTopoSidecarEntry[]
}

/**
 * 读取可选字符串。
 *
 * @param value 原始字段值。
 * @returns 非空字符串；无效时返回 `undefined`。
 */
function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 读取有限数字数组。
 *
 * @param value 原始数组。
 * @returns 数字数组；无效时返回 `null`。
 */
function readFiniteNumberArray(value: unknown): number[] | null {
  if (!Array.isArray(value)) {
    return null
  }

  for (const item of value) {
    if (typeof item !== 'number' || !Number.isFinite(item)) {
      return null
    }
  }
  // JSON.parse 已经创建了普通数组；校验后直接复用该数组，避免大 topo 文件再复制一份临时 number[]。
  return value as number[]
}

/**
 * 读取非负整数数组。
 *
 * @param value 原始数组。
 * @returns 非负整数数组；无效时返回 `null`。
 */
function readNonNegativeIntegerArray(value: unknown): number[] | null {
  if (!Array.isArray(value)) {
    return null
  }

  for (const item of value) {
    if (!Number.isInteger(item) || item < 0) {
      return null
    }
  }
  // 与 positions/normals 一样，校验后复用 JSON 数组，真正的数据复制只发生在 TypedArray 构造阶段。
  return value as number[]
}

/**
 * 判断索引是否只是顺序展开。
 *
 * @param indices 索引数组。
 * @returns 是否为顺序索引。
 */
function isSequentialIndexBuffer(indices: number[]): boolean {
  for (let index = 0; index < indices.length; index += 1) {
    if (indices[index] !== index) {
      return false
    }
  }
  return indices.length > 0
}

/**
 * 归一化 meshData.faceTriangleRanges。
 *
 * @param value 原始范围集合。
 * @returns 归一化范围集合。
 */
function readMeshFaceTriangleRanges(value: unknown): [number, number][] {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map(readImportedTriangleRangeTuple)
    .filter((range): range is [number, number] => Boolean(range))
}

/**
 * 计算三角面数量。
 *
 * @param positions 位置数组。
 * @param indices 索引数组。
 * @returns 三角面数量。
 */
function resolveMeshTriangleCount(positions: Float32Array, indices: Uint32Array | undefined): number {
  return indices ? Math.floor(indices.length / 3) : Math.floor(positions.length / 9)
}

/**
 * 判断区间是否落在三角面数量内。
 *
 * @param range 三角区间。
 * @param triangleCount 三角数量。
 * @returns 是否有效。
 */
function isTriangleRangeInsideMesh(range: [number, number], triangleCount: number): boolean {
  return range[1] <= triangleCount
}

/**
 * 统计两组面区间不一致的数量。
 *
 * @param left 左侧范围集合。
 * @param right 右侧范围集合。
 * @returns 不一致数量。
 */
function countFaceRangeMismatches(
  left: Array<[number, number] | null>,
  right: [number, number][],
): number {
  const compareLength = Math.max(left.length, right.length)
  let mismatchCount = 0
  for (let index = 0; index < compareLength; index += 1) {
    const leftRange = left[index]
    const rightRange = right[index]
    if (!leftRange) {
      continue
    }
    if (!rightRange || leftRange[0] !== rightRange[0] || leftRange[1] !== rightRange[1]) {
      mismatchCount += 1
    }
  }
  return mismatchCount
}

/**
 * 归一化 topologySnapshot.faces。
 *
 * 设计说明：
 * 1. FeatureResultMesh 的拾取映射以 `topologySnapshot.faces[].triangleRange` 为准；
 * 2. topo sidecar 的可拾取几何来自 `meshData`，因此 face range 必须与 `meshData.faceTriangleRanges` 对齐；
 * 3. 若后端同时给出 GLB 可见范围，应保留在 `visibleFaceTriangleRanges` 中给 FeatureFace 显示几何使用，不能混入 pick host 范围。
 *
 * @param rawSnapshot 原始拓扑快照。
 * @param meshFaceTriangleRanges meshData 归一化范围。
 * @returns 归一化拓扑快照。
 */
function normalizeTopologySnapshot(
  rawSnapshot: Record<string, unknown>,
  meshFaceTriangleRanges: [number, number][],
): TopologySnapshot {
  const vertices = Array.isArray(rawSnapshot.vertices) ? rawSnapshot.vertices : []
  const edges = Array.isArray(rawSnapshot.edges) ? rawSnapshot.edges : []
  const rawFaces = Array.isArray(rawSnapshot.faces) ? rawSnapshot.faces : []
  const faces = rawFaces
    .map((faceValue, faceArrayIndex) => {
      const faceRecord = asRecord(faceValue)
      if (!faceRecord) {
        return null
      }
      const triangleRange = meshFaceTriangleRanges[faceArrayIndex]
        ?? readImportedTriangleRangeTuple(faceRecord.triangleRange)
      if (!triangleRange) {
        return null
      }
      return {
        ...faceRecord,
        index: Number.isInteger(faceRecord.index) ? faceRecord.index : faceArrayIndex,
        triangleRange,
      } as unknown as TopologySnapshotFace
    })
    .filter((face): face is TopologySnapshotFace => Boolean(face))

  return {
    ...rawSnapshot,
    vertices: vertices as TopologySnapshotVertex[],
    edges: edges as TopologySnapshotEdge[],
    faces,
  } as TopologySnapshot
}

/**
 * 读取原始 topologySnapshot face range。
 *
 * @param rawSnapshot 原始拓扑快照。
 * @returns 原始 face range 集合。
 */
function readRawTopologyFaceRanges(rawSnapshot: Record<string, unknown>): Array<[number, number] | null> {
  const rawFaces = Array.isArray(rawSnapshot.faces) ? rawSnapshot.faces : []
  return rawFaces
    .map((faceValue) => readImportedTriangleRangeTuple(asRecord(faceValue)?.triangleRange))
}

/**
 * 解析单个交互 topo entry。
 *
 * @param key entry key。
 * @param value 原始 entry。
 * @returns 归一化 entry；无效时返回 `null`。
 */
function parseImportedInteractiveTopoSidecarEntry(
  key: string,
  value: unknown,
): ImportedTopoSidecarEntry | null {
  const entryRecord = asRecord(value)
  if (!entryRecord) {
    return null
  }

  const payloadRecord = asRecord(entryRecord.payload)
  const meshDataRecord = asRecord(payloadRecord?.meshData)
  const rawSnapshot = asRecord(payloadRecord?.topologySnapshot)
  if (!payloadRecord || !meshDataRecord || !rawSnapshot) {
    return null
  }

  const positionNumbers = readFiniteNumberArray(meshDataRecord.positions)
  if (!positionNumbers || positionNumbers.length === 0 || positionNumbers.length % 3 !== 0) {
    return null
  }

  const normalNumbers = readFiniteNumberArray(meshDataRecord.normals)
  const indexNumbers = readNonNegativeIntegerArray(meshDataRecord.indices)
  const hasIndexBuffer = Boolean(indexNumbers && indexNumbers.length > 0)
  const sequentialIndexBuffer = hasIndexBuffer && indexNumbers ? isSequentialIndexBuffer(indexNumbers) : false
  const positions = new Float32Array(positionNumbers)
  const normals = normalNumbers && normalNumbers.length === positionNumbers.length
    ? new Float32Array(normalNumbers)
    : new Float32Array(positionNumbers.length)
  const indices = hasIndexBuffer && indexNumbers && !sequentialIndexBuffer ? new Uint32Array(indexNumbers) : undefined
  const triangleCount = resolveMeshTriangleCount(positions, indices)
  const meshFaceTriangleRanges = readMeshFaceTriangleRanges(meshDataRecord.faceTriangleRanges)
    .filter((range) => isTriangleRangeInsideMesh(range, triangleCount))
  const topologySnapshot = normalizeTopologySnapshot(rawSnapshot, meshFaceTriangleRanges)
  const visibleFaceTriangleRanges = readImportedTopologyFaceTriangleRangeBindings(
    payloadRecord.visibleFaceTriangleRanges,
    readOptionalString(payloadRecord.bodyId),
  )

  const encoding = readOptionalString(entryRecord.encoding)
  const byteLength = typeof entryRecord.byteLength === 'number' && Number.isFinite(entryRecord.byteLength)
    ? entryRecord.byteLength
    : undefined
  const sha256 = readOptionalString(entryRecord.sha256)

  return {
    key,
    ...(encoding ? { encoding } : {}),
    ...(typeof byteLength === 'number' ? { byteLength } : {}),
    ...(sha256 ? { sha256 } : {}),
    payload: {
      bodyId: readOptionalString(payloadRecord.bodyId),
      objectId: readOptionalString(payloadRecord.objectId),
      meshData: {
        id: readOptionalString(payloadRecord.objectId) ?? key,
        positions,
        normals,
        indices,
        faceTriangleRanges: meshFaceTriangleRanges,
        topologySnapshot,
        metadata: {
          objectId: readOptionalString(payloadRecord.objectId) ?? key,
          source: 'imported-topo-sidecar',
          topologySidecarEntryKey: key,
        },
      },
      visibleFaceTriangleRanges,
      diagnostics: {
        sequentialIndexBuffer,
        topologyFaceRangeMismatchCount: countFaceRangeMismatches(
          readRawTopologyFaceRanges(rawSnapshot),
          meshFaceTriangleRanges,
        ),
      },
    },
  }
}

/**
 * 解析 topo sidecar JSON 文本。
 *
 * @param rawText topo sidecar 原始文本。
 * @returns 归一化交互拓扑 sidecar 文档。
 * @throws 当 JSON 无法解析、缺少 entries 字段或没有有效交互拓扑 entry 时抛出错误。
 */
export function parseImportedTopoSidecar(rawText: string): ImportedTopoSidecarDocument {
  let parsed: unknown

  try {
    parsed = JSON.parse(rawText)
  } catch (error) {
    throw new Error(`Topo sidecar JSON 解析失败: ${error instanceof Error ? error.message : String(error)}`)
  }

  const record = asRecord(parsed)
  const rawEntries = asRecord(record?.entries)
  if (!rawEntries) {
    throw new Error('Topo sidecar 缺少 entries 字段')
  }

  const entries: Record<string, ImportedTopoSidecarEntry> = {}
  Object.entries(rawEntries).forEach(([key, value]) => {
    const entry = parseImportedInteractiveTopoSidecarEntry(key, value)
    if (!entry) {
      return
    }
    entries[key] = entry
  })

  const interactiveEntries = Object.values(entries)
  if (interactiveEntries.length === 0) {
    throw new Error('Topo sidecar 未解析出任何交互拓扑 entry')
  }

  return {
    entries,
    interactiveEntries,
  }
}

